import { styled } from '@mui/material/styles';
import { useTranslation } from 'react-i18next';
import * as React from 'react';
import { ITourDetail } from '@lialo/core/lib/models';

import { ImageSlide, ImageSlider, SliderImage } from '../../../common/Slider';
import useTourImageWidth from '../../../common/hooks/useTourImageWidth';
import { IRatingVM } from '@lialo/core/lib/pages/tourDetail/models';
import { TourPriceTag } from '../../../common/TourPriceTag';
import { MainSlide } from './MainSlide';
import { ScrollablePageContent } from '@lialo/pwa-ui/lib/PageComponents/Page';
import { scrollToElement } from '../../../services/utils/scroll';
import { chooseTourImageLink } from '@lialo/core/lib/utils/helper/image-link';

const StyledPriceTag = styled(TourPriceTag)(({ theme }) => ({
  position: 'absolute',
  bottom: theme.spacing(1),
  left: theme.spacing(1),
}));

interface IProps {
  tourCode: string;
  tour: ITourDetail;
  tourRatings: IRatingVM[];
  numberOfComments: number;
}

const SmoothScrollingContainer = styled(ScrollablePageContent)({
  scrollBehavior: 'smooth',
});

const TourDetailContent = ({ tourCode, tourRatings, tour, numberOfComments }: IProps) => {
  const { t } = useTranslation();
  const width = useTourImageWidth();
  const scrollRef = React.useRef<HTMLDivElement>(null);

  return (
    <SmoothScrollingContainer ref={scrollRef}>
      {tour.mainImages?.length > 0 && (
        <ImageSlider
          overlay={
            <StyledPriceTag
              tourVisibility={tour.visibility}
              purchased={tour.purchased}
              price={tour.price}
              size={'large'}
            />
          }
        >
          {tour.mainImages.map((img, idx) => (
            <ImageSlide key={img.orderId} caption={img.caption} backgroundImg={chooseTourImageLink(img.url, width)}>
              <SliderImage
                originalSrc={img.url}
                alt={img.caption ?? t('tour_content.fallback_img_alt', { title: tour.title })}
                width={width}
                aspectRatio={'16 / 9'}
                slideNumber={idx}
                animate={true}
              />
            </ImageSlide>
          ))}
        </ImageSlider>
      )}
      <MainSlide
        key={'main'}
        tourCode={tourCode}
        tour={tour}
        tourRatings={tourRatings}
        numberOfComments={numberOfComments}
        onStarsClicked={() => scrollToElement('reviewSection', scrollRef.current)}
      />
    </SmoothScrollingContainer>
  );
};

export default TourDetailContent;
